/**
 * Las secciones de «Todo lo demás».
 *
 * The menu was one row per screen: twenty-one for the owner, and every one of them opened a
 * screen whose «Volver» went home. Five were already on the home screen. Six were pairs that
 * already linked to each other — Compras and «Mercadería comprada», Inventario and Vencimientos,
 * Contabilidad and Gastos — so the list was long mostly by saying things twice. And looking at
 * two things meant home, menu, the first thing, home, menu, the second.
 *
 * So screens that serve the same job make one section, and a section is one row. Inside it the
 * screens are tabs across the top, one tap apart; «Volver» from any of them goes back to where
 * the section was opened, not home, since a tab takes the place of the last in the history
 * instead of adding a step (`ui/Pestanas.tsx`, `lib/volver.ts`). Every screen keeps its own
 * address, so the alerts on the home screen, the tutorial and the links between screens land
 * exactly where they always did, just with the tabs above.
 *
 * A tab appears only for somebody who can use it, the rule the menu already had. A section with
 * one tab left is a plain screen, because a strip with one choice in it is not a choice.
 */

import type { Permission } from '@abastece/shared';
import type { Clave } from '../i18n/index.js';
import type { NombreIcono, Tinte } from '../ui/Iconos.js';

export type IdSeccion = 'mostrador' | 'productos' | 'inventario' | 'compras' | 'clientes' | 'dinero' | 'analisis';

/** The screens a tab can open. */
export type RutaPestana =
  | '/ventas'
  | '/caja'
  | '/recargas'
  | '/control'
  | '/productos'
  | '/precios'
  | '/fotos'
  | '/paquetes'
  | '/ofertas'
  | '/inventario'
  | '/vencimientos'
  | '/reponer'
  | '/compras'
  | '/proveedores'
  | '/clientes'
  | '/fiado'
  | '/pedidos'
  | '/contabilidad'
  | '/efectivo'
  | '/gastos'
  | '/planilla'
  | '/analisis';

export interface Pestana {
  ruta: RutaPestana;
  /** The screen's own title where it is short enough, so a tab and its page say the same thing. */
  nombre: Clave;
  /** Any one of these is enough: the permission the screen itself enforces. */
  permisos: readonly Permission[];
}

export interface Seccion {
  id: IdSeccion;
  icono: NombreIcono;
  tinte: Tinte;
  pestanas: readonly Pestana[];
  /**
   * Screens inside the section that are not tabs: a form, a detail, a count. Going into one and
   * coming back out is still being in the section. A trailing slash means everything under it.
   */
  dentro: readonly string[];
}

export const SECCIONES: readonly Seccion[] = [
  {
    id: 'mostrador',
    icono: 'caja',
    tinte: 'marca',
    pestanas: [
      { ruta: '/ventas', nombre: 'ventas.titulo', permisos: ['vender'] },
      { ruta: '/caja', nombre: 'caja.titulo', permisos: ['abrir_caja'] },
      { ruta: '/recargas', nombre: 'recargas.titulo', permisos: ['vender'] },
      // Who voided, refunded, brought prices down or came up short: the owner's view of the counter.
      { ruta: '/control', nombre: 'control.titulo', permisos: ['ver_reportes'] },
    ],
    // A queue, not a place: it only has something in it when a transfer is waiting, and then the
    // home screen and «Ventas» both say so. And the till's wizards, starting a shift, ending one or
    // taking one over, and the shifts gone by: steps of «Caja», not places of their own; and the
    // top-ups sold, of «Recargas».
    dentro: ['/transferencias', '/caja/', '/recargas/'],
  },
  {
    id: 'productos',
    icono: 'productos',
    tinte: 'cielo',
    pestanas: [
      { ruta: '/productos', nombre: 'productos.titulo', permisos: ['editar_productos'] },
      { ruta: '/precios', nombre: 'seccion.precios', permisos: ['editar_precios'] },
      // What still has no photo, a «Tomar foto» on each (the owner, 2026-10-20): whoever can change
      // a product's photo on its page.
      { ruta: '/fotos', nombre: 'fotos.titulo', permisos: ['editar_productos'] },
      // The pack sizes the sales ask for (the owner, 2026-10-21): whoever adds a presentation on a
      // product's page.
      { ruta: '/paquetes', nombre: 'paquetes.titulo', permisos: ['editar_productos'] },
      // What to put on sale, how much off and for how long (the owner, 2026-11-03): whoever sets the prices.
      { ruta: '/ofertas', nombre: 'ofertas.titulo', permisos: ['editar_precios'] },
    ],
    dentro: ['/productos/', '/carga-rapida', '/listas-precio'],
  },
  {
    id: 'inventario',
    icono: 'inventario',
    tinte: 'cielo',
    pestanas: [
      { ruta: '/inventario', nombre: 'inv.titulo', permisos: ['ajustar_inventario'] },
      { ruta: '/vencimientos', nombre: 'venc.titulo', permisos: ['ajustar_inventario'] },
    ],
    dentro: ['/inventario/'],
  },
  {
    id: 'compras',
    icono: 'compras',
    tinte: 'cielo',
    pestanas: [
      // What to buy first: it is what somebody opening purchases usually came to find out.
      { ruta: '/reponer', nombre: 'seccion.reponer', permisos: ['recibir_compras'] },
      { ruta: '/compras', nombre: 'compras.titulo', permisos: ['recibir_compras'] },
      { ruta: '/proveedores', nombre: 'prov.titulo', permisos: ['recibir_compras'] },
    ],
    // A purchase's steps, and a supplier's own page (2026-10-16).
    dentro: ['/compras/', '/proveedores/'],
  },
  {
    id: 'clientes',
    icono: 'clientes',
    tinte: 'violeta',
    pestanas: [
      { ruta: '/clientes', nombre: 'clientes.titulo', permisos: ['gestionar_clientes'] },
      // Everything the clients owe (the owner, 2026-10-07): the owner's at first, as giving credit
      // is; whoever looks after the clients since it went on the cashier's home screen (2026-10-08).
      { ruta: '/fiado', nombre: 'porCobrar.titulo', permisos: ['gestionar_clientes'] },
      { ruta: '/pedidos', nombre: 'pedidos.titulo', permisos: ['gestionar_clientes'] },
      // «Reparto» was the third, until the owner took it out (2026-10-04): no trucks, no route.
    ],
    // A client's own page: their account, their fiado.
    dentro: ['/clientes/'],
  },
  {
    id: 'dinero',
    icono: 'ganancia',
    tinte: 'esmeralda',
    pestanas: [
      { ruta: '/contabilidad', nombre: 'conta.titulo', permisos: ['ver_reportes'] },
      { ruta: '/efectivo', nombre: 'efectivo.titulo', permisos: ['recibir_efectivo'] },
      { ruta: '/gastos', nombre: 'seccion.gastos', permisos: ['ver_reportes'] },
      { ruta: '/planilla', nombre: 'planilla.titulo', permisos: ['gestionar_planilla'] },
    ],
    // The owner's routines, a step at a time: steps of the section, not places of their own. And
    // the whole of a list one of its screens keeps the latest of: the deposits, the payments to
    // suppliers, the owner's money.
    dentro: ['/dinero/', '/efectivo/', '/gastos/'],
  },
  {
    // How the shop is doing, as charts (the owner, 2026-10-06). A section of one, so a plain
    // screen: «Dinero» already has the four tabs a 320 px phone holds.
    id: 'analisis',
    icono: 'analisis',
    tinte: 'esmeralda',
    pestanas: [{ ruta: '/analisis', nombre: 'analisis.titulo', permisos: ['ver_reportes'] }],
    dentro: [],
  },
];

type Puede = (permiso: Permission) => boolean;

export const permitida = (p: { permisos: readonly Permission[] }, puede: Puede): boolean =>
  p.permisos.length === 0 || p.permisos.some(puede);

/** The sections somebody can use, each with only the tabs they can use, in menu order. */
export function seccionesPara(puede: Puede): { seccion: Seccion; pestanas: Pestana[] }[] {
  return SECCIONES.map((seccion) => ({
    seccion,
    pestanas: seccion.pestanas.filter((p) => permitida(p, puede)),
  })).filter((s) => s.pestanas.length > 0);
}

/**
 * A section's name is what is in it: «Productos y precios», «Ventas, caja y recargas».
 *
 * Built from the tabs the person can see, so it can't promise one they will not find: a cashier
 * sees «Ventas, caja y recargas», the owner «Ventas, caja, recargas y control». And the words are
 * the tabs' words, which are the screens' titles, so what was tapped is what opens.
 */
export function nombreSeccion(nombres: readonly string[], locale: string): string {
  const [primero, ...resto] = nombres;
  if (primero === undefined) return '';
  return new Intl.ListFormat(locale, { type: 'conjunction' }).format([
    primero,
    ...resto.map((n) => n.toLocaleLowerCase(locale)),
  ]);
}

const bajo = (pathname: string, ruta: string) =>
  ruta.endsWith('/') ? pathname.startsWith(ruta) : pathname === ruta;

/** The section a screen belongs to, whether as one of its tabs or from inside it. */
export function seccionDe(pathname: string): Seccion | undefined {
  return SECCIONES.find(
    (s) => s.pestanas.some((p) => p.ruta === pathname) || s.dentro.some((d) => bajo(pathname, d)),
  );
}

/** The section a screen is a tab of, and which tab. */
export function pestanaDe(pathname: string): { seccion: Seccion; indice: number } | undefined {
  for (const seccion of SECCIONES) {
    const indice = seccion.pestanas.findIndex((p) => p.ruta === pathname);
    if (indice >= 0) return { seccion, indice };
  }
  return undefined;
}
